import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CheckCircle2, AlertCircle, Loader2, Info, ArrowLeft, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectMeta = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    access_token: "",
    ad_account_id: "",
    page_id: ""
  });

  // Verificar status de conexão ao carregar
  useEffect(() => {
    checkConnectionStatus();
  }, [clientId]);

  const checkConnectionStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/meta/${clientId}`);
      if (response.data.connected) {
        setConnected(true);
        setConnectionInfo(response.data);
      }
    } catch (error) {
      console.error("Erro ao verificar status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Tem certeza que deseja desconectar sua conta Meta?")) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/meta/${clientId}`);
      toast.success("Meta desconectado com sucesso!");
      setConnected(false);
      setConnectionInfo(null);
      setFormData({ access_token: "", ad_account_id: "", page_id: "" });
    } catch (error) {
      console.error(error);
      toast.error("Erro ao desconectar");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!formData.access_token || !formData.ad_account_id) {
      toast.error("Preencha Access Token e Ad Account ID");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/meta/connect/${clientId}`, formData);
      
      // Mostrar informações da conta validada
      const accountInfo = response.data.account_info;
      if (accountInfo?.account_name) {
        toast.success(`✅ Conectado: ${accountInfo.account_name}`);
      } else {
        toast.success("Meta conectado com sucesso!");
      }
      
      setConnected(true);
      checkConnectionStatus(); // Recarregar status
      
      setTimeout(() => {
        navigate(`/dashboard/${clientId}`);
      }, 2000);
    } catch (error) {
      console.error(error);
      
      // Mostrar erro detalhado da validação
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object') {
        toast.error(`❌ ${errorDetail.message || 'Erro de validação'}`, {
          description: errorDetail.error || errorDetail.suggestion,
          duration: 8000
        });
      } else {
        toast.error(errorDetail || "Erro ao conectar Meta");
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Verificando conexão Meta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🎯 Conectar Meta (Facebook/Instagram)
          </h1>
          <p className="text-gray-600">
            Conecte sua conta de anúncios para criar campanhas automáticas
          </p>
        </div>

        {/* Already Connected Status */}
        {connected && connectionInfo && (
          <Card className={`mb-6 border-2 ${connectionInfo.validated ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${connectionInfo.validated ? 'text-green-900' : 'text-yellow-900'}`}>
                <CheckCircle2 className="h-6 w-6" />
                {connectionInfo.validated ? 'Meta Conectado e Validado!' : 'Meta Conectado (Não Validado)'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  {connectionInfo.validated ? (
                    <p className="text-green-800">
                      ✅ Credenciais validadas com a API do Meta!
                    </p>
                  ) : (
                    <p className="text-yellow-800">
                      ⚠️ Conexão salva mas não foi possível validar. Teste a conexão.
                    </p>
                  )}
                  {connectionInfo.account_info?.account_name && (
                    <p className="text-sm text-green-700 mt-1 font-medium">
                      📊 Conta: {connectionInfo.account_info.account_name}
                    </p>
                  )}
                  {connectionInfo.account_info?.business_name && (
                    <p className="text-sm text-green-700">
                      🏢 Empresa: {connectionInfo.account_info.business_name}
                    </p>
                  )}
                  {connectionInfo.has_account_id && (
                    <p className="text-sm text-green-700 mt-1">
                      ✓ ID da Conta de Anúncios configurado
                    </p>
                  )}
                  {connectionInfo.has_page_id && (
                    <p className="text-sm text-green-700 mt-1">
                      ✓ Page ID configurado (pode criar criativos)
                    </p>
                  )}
                  {!connectionInfo.has_page_id && (
                    <p className="text-sm text-yellow-600 mt-1">
                      ⚠️ Page ID não configurado (criativos limitados)
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const res = await axios.post(`${API}/meta/test/${clientId}`);
                        if (res.data.success) {
                          toast.success(`✅ Conexão OK! Conta: ${res.data.account_name}`);
                        } else {
                          toast.error(`❌ ${res.data.error}`);
                        }
                      } catch (err) {
                        toast.error("Erro ao testar conexão");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="border-blue-500 text-blue-700 hover:bg-blue-100"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "🔌 Testar"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={checkConnectionStatus}
                    className="border-green-500 text-green-700 hover:bg-green-100"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Atualizar
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleDisconnect}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Desconectar
                  </Button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => navigate(`/campaigns/${clientId}`)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Ver Campanhas
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/dashboard/${clientId}`)}
                >
                  Voltar ao Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Just connected message */}
        {connected && !connectionInfo && (
          <Alert className="mb-6 border-green-500 bg-green-50">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-800 font-medium">
              ✅ Meta conectado! Redirecionando para dashboard...
            </AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Formulário Manual */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge variant="outline">Opção 1</Badge>
                Conexão Manual
              </CardTitle>
              <CardDescription>
                Cole seu token de acesso e ID da conta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="access_token" className="text-base">
                  Access Token
                </Label>
                <Input
                  id="access_token"
                  type="password"
                  placeholder="EAAxxxxxxxxxxxxx..."
                  value={formData.access_token}
                  onChange={(e) => setFormData({...formData, access_token: e.target.value})}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Token de longa duração do Meta for Developers
                </p>
              </div>

              <div>
                <Label htmlFor="ad_account_id" className="text-base">
                  ID da Conta de Anúncios
                </Label>
                <Input
                  id="ad_account_id"
                  placeholder="act_1234567890"
                  value={formData.ad_account_id}
                  onChange={(e) => setFormData({...formData, ad_account_id: e.target.value})}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Começa com "act_" ou apenas números
                </p>
              </div>

              <div>
                <Label htmlFor="page_id" className="text-base">
                  ID da Página do Facebook <Badge variant="secondary" className="ml-2">Opcional</Badge>
                </Label>
                <Input
                  id="page_id"
                  placeholder="1234567890123456"
                  value={formData.page_id}
                  onChange={(e) => setFormData({...formData, page_id: e.target.value})}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Necessário para criar anúncios com criativos
                </p>
              </div>

              <Button 
                onClick={handleConnect}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                disabled={loading || connected}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  "Conectar Conta"
                )}
              </Button>

              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate("/meta-setup-guide")}
              >
                📘 Ver Guia Completo de Configuração
              </Button>
            </CardContent>
          </Card>

          {/* Guia */}
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Info className="h-5 w-5" />
                Como obter suas credenciais?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <h3 className="font-bold text-blue-900 mb-2">
                  📝 Guia Completo Passo a Passo:
                </h3>
                
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded border border-blue-200">
                    <p className="font-medium text-blue-900 mb-2">
                      1️⃣ Obter Access Token
                    </p>
                    <div className="space-y-2 text-gray-700 text-xs">
                      <p className="font-medium">Opção A - Via Graph API Explorer:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Acesse: <code className="bg-gray-100 px-1 text-[10px]">developers.facebook.com/tools/explorer</code></li>
                        <li>Faça login na sua conta Meta</li>
                        <li>Clique em "Generate Access Token"</li>
                        <li>Marque: <code className="bg-gray-100 px-1">ads_management</code></li>
                        <li>Copie o token</li>
                      </ol>
                      
                      <p className="font-medium mt-3">Opção B - Via Apps Facebook:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Acesse: <code className="bg-gray-100 px-1 text-[10px]">developers.facebook.com/apps</code></li>
                        <li>Crie um app ou use existente</li>
                        <li>Vá em "Ferramentas" → "Access Token Tool"</li>
                        <li>Gere token com permissão de ads</li>
                      </ol>
                      
                      <div className="bg-yellow-50 border border-yellow-200 p-2 rounded mt-2">
                        <p className="text-[10px] text-yellow-800">
                          ⚠️ Se os sites não carregarem, pode ser instabilidade temporária do Facebook. 
                          Tente novamente em alguns minutos.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded border border-blue-200">
                    <p className="font-medium text-blue-900 mb-2">
                      2️⃣ Obter ID da Conta de Anúncios
                    </p>
                    <div className="space-y-2 text-gray-700 text-xs">
                      <p className="font-medium">Via Business Manager:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Acesse: <code className="bg-gray-100 px-1 text-[10px]">business.facebook.com</code></li>
                        <li>Faça login</li>
                        <li>Clique em "Configurações" (ícone de engrenagem)</li>
                        <li>No menu lateral: "Contas de Anúncios"</li>
                        <li>Veja seu ID (ex: <code className="bg-gray-100 px-1">act_1234567890</code>)</li>
                      </ol>
                      
                      <p className="font-medium mt-3">Via Gerenciador de Anúncios:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Acesse: <code className="bg-gray-100 px-1 text-[10px]">facebook.com/adsmanager</code></li>
                        <li>Clique no nome da conta (canto superior esquerdo)</li>
                        <li>O ID aparece embaixo do nome</li>
                        <li>Copie incluindo o "act_"</li>
                      </ol>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded border border-green-200">
                    <p className="font-medium text-green-900 mb-1">
                      💡 Não tem conta de anúncios ainda?
                    </p>
                    <p className="text-xs text-gray-700">
                      1. Acesse facebook.com/business<br/>
                      2. Crie um Business Manager<br/>
                      3. Adicione uma conta de anúncios<br/>
                      4. Configure método de pagamento
                    </p>
                  </div>
                </div>
              </div>

              <Alert className="border-yellow-500 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-xs text-yellow-800">
                  <strong>Importante:</strong> O token precisa ter permissão <code>ads_management</code> e ser de longa duração (60 dias).
                </AlertDescription>
              </Alert>

              <div className="bg-gray-100 p-3 rounded border border-gray-300">
                <p className="text-xs font-medium text-gray-700 mb-2">📋 URLs para copiar e colar no navegador:</p>
                <div className="space-y-1">
                  <div className="bg-white p-2 rounded border border-gray-200">
                    <p className="text-[10px] text-gray-500 mb-1">Graph API Explorer:</p>
                    <code className="text-xs text-blue-600 break-all select-all">
                      https://developers.facebook.com/tools/explorer
                    </code>
                  </div>
                  <div className="bg-white p-2 rounded border border-gray-200">
                    <p className="text-[10px] text-gray-500 mb-1">Business Manager:</p>
                    <code className="text-xs text-blue-600 break-all select-all">
                      https://business.facebook.com/settings/ad-accounts
                    </code>
                  </div>
                  <div className="bg-white p-2 rounded border border-gray-200">
                    <p className="text-[10px] text-gray-500 mb-1">Gerenciador de Anúncios:</p>
                    <code className="text-xs text-blue-600 break-all select-all">
                      https://facebook.com/adsmanager
                    </code>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Adicional */}
        <Card className="mt-6 border-2 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="text-purple-900">🔒 Segurança</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-purple-800 space-y-2">
            <p>
              ✅ <strong>Seus dados são seguros:</strong> Seu token é criptografado e armazenado de forma segura.
            </p>
            <p>
              ✅ <strong>Você mantém controle:</strong> Pode revogar o acesso a qualquer momento no Facebook.
            </p>
            <p>
              ✅ <strong>Permissões limitadas:</strong> Usamos apenas para criar e gerenciar suas campanhas.
            </p>
          </CardContent>
        </Card>

        {/* Opção OAuth (Futuro) */}
        <Card className="mt-6 border-2 border-gray-200 bg-gray-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge>Em Breve</Badge>
              Conexão Automática (OAuth)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600">
            <p className="mb-3">
              Em breve você poderá conectar sua conta com 1 clique, sem precisar copiar tokens manualmente!
            </p>
            <Button variant="outline" disabled className="w-full">
              🔗 Conectar com Facebook (Em breve)
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConnectMeta;
